import { Transition } from "components/shared/column";
import { Field, Select } from "components/shared/form";
import { featureModuleTypesEnum } from "helpers/enums";
import { getInitialStateFromConfig, serializeReq, validateForm } from "helpers/form";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { requiredField } from "helpers/validators";
import React, { useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { apply, editAccountant, getItem } from "sources/books/legalPersons";
import BankAccounts from "./bankAccounts";
import DetailsForm from "./detailsForm";
import DetailsView from "./detailsView";
import Management from "./management";

const config = {
    inn: { inputProps: { name: "inn" }, label: "ИНН", validators: [requiredField], required: true },
    name: { inputProps: { name: "name" }, label: "Название", validators: [requiredField], required: true },
    kpp: { inputProps: { name: "kpp" }, label: "КПП" },
    email: { label: "E-mail", inputProps: { name: "email", type: "email" } },
    phoneNumber: { label: "Номер телефона", inputProps: { name: "phoneNumber" }, type: "phone" },
    ogrn: { label: "ОГРН/ОГРНИП", inputProps: { name: "ogrn" } },
    address: { label: "Адрес", inputProps: { name: "address" } },
    city: { label: "Город", inputProps: { name: "city" } },
    street: { label: "Улица", inputProps: { name: "street" } },
    houseNumber: { label: "Номер дома", inputProps: { name: "houseNumber" } },
    paymentTypeId: {
        label: "Тип оплаты",
        inputProps: { name: "paymentTypeId" },
        dictionary: "paymentType",
        menuPortalTarget: document.body,
    },
    ndsTypeId: {
        label: "Тип НДС",
        inputProps: { name: "ndsTypeId" },
        dictionary: "nDSType",
        menuPortalTarget: document.body,
    },
};

const LegalPersonForm = (props) => {
    const [pageLoading, setPageLoading] = useState(true);
    const [detailsSaving, setDetailsSaving] = useState(false);
    const [valid, setValid] = useState(true);
    const [data, setData] = useState({});
    const [detailsDraft, setDetailsDraft] = useState({});
    const [manual, setManual] = useState(false);
    const [detailsEditMode, setDetailsEditMode] = useState(false);

    const { id } = props.match.params;
    const refreshData = ({ showPageLoader = false } = {}) => {
        if (id) {
            showPageLoader && setPageLoading(true);
            return getItem(id)
                .then((nextData) => {
                    setData(nextData);
                    return nextData;
                })
                .catch(() => undefined)
                .finally(() => setPageLoading(false));
        }
        setPageLoading(false);
        return Promise.resolve();
    };

    const handleSelect = ({ option }, editMode = false) => {
        if (!option) {
            return;
        }
        if (editMode) {
            setDetailsDraft({ ...detailsDraft, ...option });
        } else {
            setData({ ...data, ...option });
        }
    };

    useEffect(() => {
        refreshData({ showPageLoader: true });
    }, []);

    const back = () => props.history.push(paths.legalPersons);

    const isValidForm = (state) => {
        const isValid = validateForm(props.config, serializeReq(state, config));
        setValid(isValid);
        return isValid;
    };

    const saveData = (e) => {
        e.preventDefault();
        if (isValidForm(data)) {
            setPageLoading(true);
            apply(data)
                .catch(() => undefined)
                .finally(() => setPageLoading(false));
        }
    };

    const onFieldChange = ({ name, value }) => setData({ ...data, [name]: value });

    const onDetailsFieldChange = ({ name, value }) => setDetailsDraft({ ...detailsDraft, [name]: value });

    const startDetailsEdit = () => {
        setDetailsDraft(data);
        setValid(true);
        setDetailsEditMode(true);
    };

    const cancelDetailsEdit = () => {
        setDetailsDraft({});
        setValid(true);
        setDetailsEditMode(false);
    };

    const saveDetails = (e) => {
        e.preventDefault();
        if (!isValidForm(detailsDraft)) {
            return;
        }

        setDetailsSaving(true);
        apply(detailsDraft)
            .then((res) => {
                if (res) {
                    setDetailsEditMode(false);
                    return refreshData();
                }
            })
            .catch(() => undefined)
            .finally(() => setDetailsSaving(false));
    };

    const renderField = (name, editMode = false) => (
        <Field
            {...props.config[name]}
            value={editMode ? detailsDraft[name] : data[name]}
            onChange={editMode ? onDetailsFieldChange : onFieldChange}
            disabled={!manual && ["inn", "kpp", "name"].includes(name)}
        />
    );

    const renderSelect = (name, editMode = false) => {
        const selectProps = props.config[name];
        selectProps.dictionary && (selectProps.options = props.enums[selectProps.dictionary] || []);
        return (
            <Select
                {...props.config[name]}
                value={editMode ? detailsDraft[name] : data[name]}
                onChange={editMode ? onDetailsFieldChange : onFieldChange}
            />
        );
    };

    const renderHead = () => {
        return (
            <div className="book-show__head">
                <div className="book-show__close close close--sm" onClick={back} />
                <div className="book-show__title">{id ? "Организация" : "Создание организации"}</div>
            </div>
        );
    };

    const renderBody = () => {
        const canShowDocuments = !!id;
        const canEditManagement = props.profile.availableFeatureModules.includes(featureModuleTypesEnum.CrStart) && id;

        if (pageLoading) {
            return <div className="card card-body loading" />;
        }

        if (!id) {
            return (
                <div className="row">
                    <div className="col-sm-12">
                        <DetailsForm
                            valid={valid}
                            loading={pageLoading}
                            manual={manual}
                            setManual={setManual}
                            renderField={(name) => renderField(name)}
                            renderSelect={(name) => renderSelect(name)}
                            onSave={saveData}
                            onSelect={(params) => handleSelect(params)}
                        />
                    </div>
                </div>
            );
        }

        return (
            <div className="row">
                <div className={canShowDocuments ? "col-lg-7 col-sm-12" : "col-sm-12"}>
                    {detailsEditMode ? (
                        <DetailsForm
                            valid={valid}
                            loading={detailsSaving}
                            manual={manual}
                            setManual={setManual}
                            renderField={(name) => renderField(name, true)}
                            renderSelect={(name) => renderSelect(name, true)}
                            onCancel={cancelDetailsEdit}
                            onSave={saveDetails}
                            onSelect={(params) => handleSelect(params, true)}
                            title="Редактирование реквизитов"
                        />
                    ) : (
                        <DetailsView
                            data={data}
                            formConfig={props.config}
                            enums={props.enums}
                            onEdit={startDetailsEdit}
                        />
                    )}
                    <BankAccounts legalPersonId={id} modal={props.modal} />
                </div>
                {canShowDocuments && (
                    <div className="col-lg-5 col-sm-12">
                        <Management
                            legalPerson={data}
                            data={data.accountant}
                            management={data.management ?? {}}
                            action={(accountant) => editAccountant({ id, accountant })}
                            onUpdate={refreshData}
                            modal={props.modal}
                            id={id}
                            showManagement={canEditManagement}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <Transition dir="full">
            <div className="page-book__show book-show">
                {renderHead()}
                <div className="book-show__body">
                    <div className="native-scroll__wrap h-100">
                        <Scrollbars>
                            <div className="native-scroll__container">{renderBody()}</div>
                        </Scrollbars>
                    </div>
                </div>
            </div>
        </Transition>
    );
};

const ConfiguredShow = (props) => (
    <LegalPersonForm
        {...props}
        config={config}
        initialState={props.initialState || getInitialStateFromConfig(config)}
    />
);

export default viewContainer(ConfiguredShow, "legal-persons");
